import axios from "axios";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { useFormik } from "formik";
import { FinalQuotationSchema } from "../../../ValidationSchema";
import { formatMoney } from "../../../utils/money";

const RevertBackShow = () => {
  const { productId, orderId } = useParams();
  const location = useLocation();
  const Navigate = useNavigate();

  const [name, setName] = useState("");
  const [status, setStatus] = useState("");
  const [id, setId] = useState(0);
  const [remark, setRemark] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [size, setSize] = useState("");
  const [Price, setPrice] = useState(0);
  const [orderassignedid, setOrderassignedid] = useState(0);
  const [quotations, setQuotation] = useState([]);
  const [orderIdData, setOrderId] = useState(0);
  const [oldPrice, setOldPrice] = useState(0);
  const [data, setData] = useState([]);
  const [quotationId, setquotationId] = useState(0);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);

  const user = JSON.parse(localStorage.getItem("user") || "{}");

  // ✅ Function to get quotations
  const AssignedOrders = (orderId, productId) => {
    if (!orderId || !productId) return;

    axios
      .get(
        `${process.env.REACT_APP_URL}/admin/quotation?order_id=${orderId}&product_id=${productId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("Token")}`,
          },
        }
      )
      .then((res) => {
        const assigned = res?.data?.data || [];
        const filtered = assigned.filter(
          (item) =>
            item?.product_id === Number(productId) && item?.supplier_id !== null
        );
        setQuotation(filtered);
      })
      .catch((err) => {
        if (err.response?.data?.message === "Unauthorized!") {
          Navigate("/");
        } else {
          toast.error("Supplier not assigned");
        }
      });
  };

  // ✅ UseEffect to support both state & params
  useEffect(() => {
    const productIdResolved = location?.state?.product_id || Number(productId);
    const orderIdResolved = location?.state?.order_id || Number(orderId);
    const orderAssignedResolved = location?.state?.order_assigned_id || 0;

    if (!productIdResolved || !orderIdResolved) {
      Navigate("/admin/dashboard");
      return;
    }

    setId(productIdResolved);
    setOrderId(orderIdResolved);
    setOrderassignedid(orderAssignedResolved);
    setName(location?.state?.title || "");
    setQuantity(location?.state?.quantity || 1);
    setSize(location?.state?.size || "");
    setRemark(location?.state?.remark || "");
    setStatus(location?.state?.status || "");
    setPrice(location?.state?.price || 0);

    AssignedOrders(orderIdResolved, productIdResolved);
  }, [productId, orderId]);

  // ✅ Edit modal handler
  const handleEditQuotesModal = (order_assigned_id, id, price) => {
    setOldPrice(price);
    setOrderassignedid(order_assigned_id);
    setShowSupplierModal(true);
  };

  const handleCloseModal = () => setShowSupplierModal(false);

  // ✅ Approve / Reject handling
  const handlePendingClick = (
    supplier_price,
    id,
    price,
    status,
    admin_quotation_price
  ) => {
    setPrice(price);
    setquotationId(id);

    if (user?.user_type === "sub_admin") {
      if (!price) {
        Swal.fire({
          icon: "warning",
          title: "Cannot Approve",
          text: "Price is not provided for this quotation.",
        });
        return;
      }

      if (
        (status === "pending" && admin_quotation_price != null) ||
        (status === null && admin_quotation_price != "") ||
        (status === undefined && admin_quotation_price != undefined)
      ) {
        Swal.fire({
          icon: "warning",
          title: "Cannot Approve",
          text: "You can't approve this quotation. Please wait for supplier approval.",
        });
        return;
      }

      setShowApprovalModal(true);
    } else {
      Swal.fire({
        title: "What would you like to do?",
        icon: "question",
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: "Approved",
        denyButtonText: "Rejected",
        cancelButtonText: "Cancel",
      }).then((result) => {
        if (result.isConfirmed) {
          if (!price) {
            Swal.fire({
              icon: "warning",
              title: "Cannot Approve",
              text: "Price is not provided for this quotation.",
            });
            return;
          }
          handleStatusUpdate(
            id,
            "accept",
            supplier_price || price,
            supplier_price ? "supplier_final_quote" : "supplier_quote"
          );
        } else if (result.isDenied) {
          handleStatusUpdate(
            id,
            "reject",
            supplier_price || price,
            supplier_price ? "supplier_final_quote" : "supplier_quote"
          );
        }
      });
    }
  };

  // ✅ Status update API
  const handleStatusUpdate = async (id, status, price, quote) => {
    try {
      const endpoint =
        quote === "supplier_final_quote"
          ? `/admin/quotation/set/finalPrice/${id}`
          : `/admin/quotation/send/supplier/finalPrice/${id}`;

      const response = await axios.post(
        `${process.env.REACT_APP_URL}${endpoint}`,
        { status, price },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("Token")}`,
          },
        }
      );

      const updatedItem = response?.data?.data;

      setData((prev) =>
        prev.map((item) =>
          item.order_assigned_id === id
            ? { ...item, status: updatedItem.status }
            : item
        )
      );

      AssignedOrders(orderId, productId);
      window.location.reload();
    } catch (err) {
      if (err.response?.data?.message === "Unauthorized!") {
        Navigate("/");
      } else {
        // This used to only console.error — a failed approve/reject/counter
        // looked like nothing happened at all, with no on-screen feedback.
        console.error("Error updating status:", err);
        toast.error(
          err.response?.data?.message || "Failed to update the quotation"
        );
      }
    }
  };

  // ✅ Admin final quotation form
  const AddFinalQuotation = useFormik({
    initialValues: { final_description: "", final_price: "" },
    validationSchema: FinalQuotationSchema,
    onSubmit: async (values, action) => {
      if (parseFloat(values?.final_price) < parseFloat(Price)) {
        Swal.fire({
          icon: "warning",
          title: "Invalid Price",
          text: `Quotation price cannot exceed ₹${Price}.`,
        });
        return;
      }

      const result = await Swal.fire({
        title: "Are you Sure?",
        text:
          status === 1
            ? "You are about to approve this quotation."
            : "You don't want to take this order?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: status === 1 ? "#007bff" : "#dc3545",
        cancelButtonColor: "#6c757d",
        confirmButtonText: status === 1 ? "Yes, Approve" : "Yes, Reject",
      });

      if (!result.isConfirmed) return;

      const data = {
        id: orderIdData,
        status,
        final_description: values.final_description,
        final_price: values.final_price,
      };

      try {
        await axios.post(
          `${process.env.REACT_APP_URL}/admin/admin-final-quotation-approved`,
          data,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("Token")}`,
            },
          }
        );

        await axios.post(
          `${process.env.REACT_APP_URL}/admin/request-quotation-approved`,
          { id: quotationId, status: data.status },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("Token")}`,
            },
          }
        );

        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Quotation updated successfully.",
          timer: 2000,
          showConfirmButton: false,
        });

        setShowApprovalModal(false);
        action.resetForm();
        window.location.reload();
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: err?.response?.data?.message || "Something went wrong",
        });
      }
    },
  });

  // ✅ Admin quotation price edit form
  const AddAdminQuotation = useFormik({
    initialValues: { admin_quotation_price: "" },
    onSubmit: async (values, action) => {
      if (oldPrice == null || oldPrice == undefined) {
        action.setFieldError(
          "admin_quotation_price",
          "Supplier has not submitted quotation yet. Please wait."
        );
        return;
      }

      if (parseFloat(values.admin_quotation_price) > parseFloat(oldPrice)) {
        Swal.fire({
          icon: "warning",
          title: "Invalid Price",
          text: `Your counter of ₹${values.admin_quotation_price} can't be higher than the current asking price of ₹${oldPrice}.`,
        });
        return;
      }

      const data = { price: values.admin_quotation_price, status: "reject" };

      try {
        await axios.post(
          `${process.env.REACT_APP_URL}/admin/quotation/send/supplier/finalPrice/${orderassignedid}`,
          data,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("Token")}`,
            },
          }
        );

        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Quotation updated successfully.",
          timer: 2000,
          showConfirmButton: false,
        });

        setShowSupplierModal(false);
        action.resetForm();
        window.location.reload();
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: err?.response?.data?.message || "Something went wrong",
        });
      }
    },
  });

  const StatusControl = ({ item }) => {
    if (item.status === "pending" || item.status === "approved_by_sub_admin") {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const canOpenModal =
        (item?.admin_quotation_price == null ||
          item.supplier_to_admin_quotation_price != null) &&
        (item.status === "pending" ||
          item.status === "approved_by_sub_admin") &&
        !(
          user.user_type === "subadmin" &&
          item.status === "approved_by_sub_admin"
        );

      const label =
        item.status === "approved_by_sub_admin"
          ? "Approved by subadmin"
          : "Approval Pending";

      // When there is nothing to act on, this is a status — render it as one.
      // Clicking used to be a silent no-op, which read as a broken button.
      if (!canOpenModal) {
        return (
          <span
            className="al-pill"
            style={{
              background: "var(--color-warning-bg)",
              color: "var(--color-warning-text)",
            }}
          >
            {label}
          </span>
        );
      }

      // Actionable: styled as a button (border + caret + hover) so it doesn't
      // read as a passive status chip.
      return (
        <button
          type="button"
          className="al-btn rb-statusbtn"
          title="Review this quote and set the admin quotation"
          style={{
            background: "var(--color-warning-bg)",
            color: "var(--color-warning-text)",
            border: "1px solid var(--color-warning-text)",
          }}
          onClick={() =>
            handlePendingClick(
              item.supplier_to_admin_quotation_price,
              item.order_assigned_id,
              item.quotation_price,
              item.status,
              item.admin_quotation_price
            )
          }
        >
          {label}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
        </button>
      );
    }
    if (item.status === "approved")
      return <span className="al-pill" style={{ background: "var(--color-success-bg)", color: "var(--color-success-text)" }}>Approved</span>;
    if (item.status === "rejected")
      return <span className="al-pill" style={{ background: "var(--color-danger-bg)", color: "var(--color-danger-text)" }}>Rejected</span>;
    if (item.status === "waiting_for_final_approval")
      return <span className="al-pill" style={{ background: "var(--color-info-bg)", color: "var(--color-info-text)" }}>Waiting for Final Approval</span>;
    return <span className="al-muted">—</span>;
  };

  return (
    <div className="al">
      <div className="al-head">
        <div>
          <h1 className="al-title">Supplier Quotes</h1>
          <p className="al-sub">Review supplier quotations for this order and set the admin final quotation.</p>
        </div>
        <div className="al-head__actions">
          <button type="button" className="al-btn" onClick={() => Navigate(-1)}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            Back
          </button>
        </div>
      </div>

      {Array.isArray(quotations) && quotations.length > 0 ? (
        <>
          <div className="bpl-buyer" style={{ marginBottom: 16 }}>
            <span className="rfq-oid">Order #{orderIdData}</span>
            <div style={{ minWidth: 0 }}>
              <div className="bpl-buyer__name" style={{ fontSize: 15 }}>{name || quotations?.[0]?.product?.title || "—"}</div>
              <div className="bpl-buyer__sub">{(size || "-")} · Qty {quantity || "-"}</div>
            </div>
            <span style={{ marginLeft: "auto" }} className="al-pill">
              {quotations.length} supplier quote{quotations.length === 1 ? "" : "s"}
            </span>
          </div>

          {quotations.map((item, index) => {
            return (
              <div className="al-card" style={{ marginBottom: 16, overflow: "hidden" }} key={index}>
                <div className="rb-qhead">
                  <h3 className="rb-qhead__title">
                    Quotation Details <span style={{ color: "var(--color-primary)" }}>#{index + 1}</span>
                  </h3>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span className="al-muted" style={{ fontSize: 11.5 }}>Status</span>
                    <StatusControl item={item} />
                  </div>
                </div>

                <div className="rb-qbody">
                  {/* Order facts on the left, money on the right — the old flat
                      4-across grid left Price alone on a full-width row with a
                      lot of dead space beside it. */}
                  <div className="rb-facts">
                    <div className="rb-field">
                      <div className="rb-field__lbl">Supplier Name</div>
                      <div className="rb-field__val">{item?.supplier_name}</div>
                    </div>
                    <div className="rb-field">
                      <div className="rb-field__lbl">Product Name</div>
                      <div className="rb-field__val">{item?.product?.title}</div>
                    </div>
                    <div className="rb-field">
                      <div className="rb-field__lbl">Size</div>
                      <div className="rb-field__val">{size || "-"}</div>
                    </div>
                    <div className="rb-field">
                      <div className="rb-field__lbl">Quantity</div>
                      <div className="rb-field__val">{quantity || "-"}</div>
                    </div>

                    <div className="rb-field">
                      <div className="rb-field__lbl">Remark</div>
                      <div className="rb-field__val">{item?.remark || "-"}</div>
                    </div>
                    {/* Date and time were two fields for one instant. */}
                    <div className="rb-field">
                      <div className="rb-field__lbl">Submitted</div>
                      <div className="rb-field__val al-mono">
                        {new Date(item.created_at).toLocaleString("en-GB", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="rb-price">
                    <div className="rb-field__lbl">Price</div>

                    {/*
                      This was one row of amounts distinguished only by
                      strikethroughs and parenthetical labels ("450 (Old Price:
                      500)"), so you could not tell who had offered what. A
                      negotiation is a sequence — show it as one, newest last,
                      naming the party on every step.
                    */}
                    {(() => {
                      const steps = [
                        { who: "Supplier quoted", amount: item?.quotation_price },
                        { who: "Your counter", amount: item?.admin_quotation_price },
                        {
                          who: "Supplier re-quoted",
                          amount: item?.supplier_to_admin_quotation_price,
                        },
                      ].filter(
                        (step) =>
                          step.amount !== null &&
                          step.amount !== undefined &&
                          step.amount !== ""
                      );

                      if (steps.length === 0) {
                        return (
                          <div style={{ marginTop: 6 }}>
                            <span
                              className="al-pill"
                              style={{
                                background: "var(--color-warning-bg)",
                                color: "var(--color-warning-text)",
                              }}
                            >
                              Awaiting supplier quote
                            </span>
                          </div>
                        );
                      }

                      const lastIndex = steps.length - 1;

                      return (
                        <div className="al-ladder">
                          {steps.map((step, i) => (
                            <div
                              key={step.who}
                              className={`al-ladder__row ${
                                i === lastIndex ? "is-current" : "is-superseded"
                              }`}
                            >
                              <span className="al-ladder__who">{step.who}</span>
                              <span className="al-ladder__amt">
                                {formatMoney(step.amount)}
                              </span>

                              {/*
                                The supplier answer belongs on the counter row.
                                This used to read `supplierquotationApproved`,
                                which getAllAssigned does not return — so none
                                of the three pills ever rendered. Derive it from
                                `status`, which does come back: accepting a
                                counter moves the assignment to
                                waiting_for_final_approval.
                              */}
                              {step.who === "Your counter" &&
                                (() => {
                                  const accepted = [
                                    "waiting_for_final_approval",
                                    "approved_by_sub_admin",
                                    "approved",
                                  ].includes(item?.status);

                                  if (accepted) {
                                    return (
                                      <span className="al-pill" style={{ background: "var(--color-success-bg)", color: "var(--color-success-text)" }}>
                                        Accepted by supplier
                                      </span>
                                    );
                                  }
                                  // A counter back from the supplier gets its
                                  // own ladder row, so it needs no pill here.
                                  if (item?.supplier_to_admin_quotation_price != null) {
                                    return null;
                                  }
                                  if (item?.status === "rejected") return null;

                                  return (
                                    <span className="al-pill" style={{ background: "var(--color-warning-bg)", color: "var(--color-warning-text)" }}>
                                      Awaiting supplier
                                    </span>
                                  );
                                })()}

                              {/* Edit sits on the newest amount, where it acts.
                                  Admin and sub-admin may both counter-negotiate
                                  (the backend's supplierAccess allows both). */}
                              {i === lastIndex &&
                                ["admin", "subadmin"].includes(
                                  user?.user_type
                                ) &&
                                (() => {
                                  // sendAdminPrice only accepts assignments still
                                  // in play — status IN ('pending',
                                  // 'approved_by_sub_admin') and send_count < 2.
                                  // Once finalised the POST 404s, which surfaced
                                  // as a bare "Something went wrong" after the
                                  // admin had filled in the whole form.
                                  const isEditable =
                                    ["pending", "approved_by_sub_admin"].includes(
                                      item?.status
                                    ) && (item?.send_count ?? 0) < 2;

                                  if (!isEditable) return null;

                                  // Already countered and waiting on the
                                  // supplier: visible but inert, as before.
                                  const isDisabled = item?.admin_quotation_price
                                    ? item?.supplierquotationApproved !== 2
                                    : false;

                                  return (
                                    <button
                                      type="button"
                                      className="al-btn"
                                      style={{
                                        height: 28,
                                        padding: "0 10px",
                                        fontSize: 11.5,
                                        opacity: isDisabled ? 0.5 : 1,
                                        cursor: isDisabled
                                          ? "not-allowed"
                                          : "pointer",
                                      }}
                                      title={
                                        isDisabled
                                          ? "Waiting for the supplier to respond to your counter"
                                          : "Counter with a different price"
                                      }
                                      onClick={
                                        isDisabled
                                          ? undefined
                                          : () =>
                                              // `step.amount` — the newest
                                              // ladder figure — not
                                              // item.quotation_price, which
                                              // stays the ORIGINAL supplier
                                              // quote even after a re-quote.
                                              // Validating a new counter
                                              // against the stale original
                                              // (rather than the current
                                              // asking price) is exactly
                                              // what produced a confusing
                                              // "cannot exceed" error for a
                                              // clearly-lower number.
                                              handleEditQuotesModal(
                                                item?.order_assigned_id,
                                                item?.supplier_id,
                                                step.amount
                                              )
                                      }
                                    >
                                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" /></svg>
                                      Counter
                                    </button>
                                  );
                                })()}
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>

                </div>
              </div>
            );
          })}
        </>
      ) : (
        <div className="al-card">
          <div className="al-empty">
            <div className="al-empty__icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><rect x="8" y="2" width="8" height="4" rx="1" /></svg>
            </div>
            <p className="al-empty__title">Supplier not assigned</p>
            <p className="al-empty__text">No supplier quotations to review for this order yet.</p>
          </div>
        </div>
      )}

      {/* Approval / Final Quotation modal */}
      {showApprovalModal && (
        <div className="al-modal" onClick={() => setShowApprovalModal(false)}>
          <div className="al-modal__scrim" />
          <div className="al-modal__box" onClick={(e) => e.stopPropagation()} style={{ width: "min(460px,92vw)" }}>
            <div className="al-modal__head">
              <h3 className="al-modal__title">Admin Quotation</h3>
              <button type="button" className="al-act" onClick={() => setShowApprovalModal(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 16 }}>
              <div className="rb-form-group">
                <label className="rb-label">Price <span style={{ color: "var(--color-danger-text)" }}>*</span></label>
                <input className="rb-input" type="text" placeholder="Enter Price"
                  value={AddFinalQuotation.values.final_price}
                  onChange={AddFinalQuotation.handleChange}
                  onBlur={AddFinalQuotation.handleBlur}
                  name="final_price" />
                {AddFinalQuotation.errors.final_price && AddFinalQuotation.touched.final_price && (
                  <div className="rb-err">{AddFinalQuotation.errors.final_price}</div>
                )}
              </div>
              <div className="rb-form-group">
                <label className="rb-label">Description</label>
                <textarea className="rb-input" placeholder="Enter description" rows="4"
                  value={AddFinalQuotation.values.final_description}
                  onChange={AddFinalQuotation.handleChange}
                  onBlur={AddFinalQuotation.handleBlur}
                  name="final_description" style={{ resize: "vertical", height: "auto", lineHeight: 1.5, padding: "11px 13px" }} />
                {AddFinalQuotation.errors.final_description && AddFinalQuotation.touched.final_description && (
                  <div className="rb-err">{AddFinalQuotation.errors.final_description}</div>
                )}
              </div>
            </div>
            <div className="al-modal__foot" style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: "14px 20px", borderTop: "1px solid var(--color-border-soft)" }}>
              <button type="button" className="al-btn"
                onClick={() => { setStatus(0); setTimeout(() => AddFinalQuotation.handleSubmit(), 0); }}
                style={{ background: "var(--color-danger-bg)", color: "var(--color-danger-text)", border: 0 }}>
                Rejected
              </button>
              <button type="button" className="al-btn al-btn--primary"
                onClick={() => { setStatus(1); setTimeout(() => AddFinalQuotation.handleSubmit(), 0); }}>
                Approved
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Quotation (supplier) modal */}
      {showSupplierModal && (
        <div className="al-modal" onClick={() => setShowSupplierModal(false)}>
          <div className="al-modal__scrim" />
          <form onSubmit={AddAdminQuotation.handleSubmit} onClick={(e) => e.stopPropagation()}>
            <div className="al-modal__box" style={{ width: "min(460px,92vw)" }}>
              <div className="al-modal__head">
                <h3 className="al-modal__title">Admin Quotation</h3>
                <button type="button" className="al-act" onClick={() => setShowSupplierModal(false)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                </button>
              </div>
              <div style={{ padding: 20 }}>
                <div className="rb-form-group">
                  <label className="rb-label">Price <span style={{ color: "var(--color-danger-text)" }}>*</span></label>
                  <input className="rb-input" type="text" placeholder="Enter Price"
                    value={AddAdminQuotation.values.admin_quotation_price}
                    onChange={AddAdminQuotation.handleChange}
                    onBlur={AddAdminQuotation.handleBlur}
                    name="admin_quotation_price" />
                  {AddAdminQuotation.errors.admin_quotation_price && (
                    <div className="rb-err">{AddAdminQuotation.errors.admin_quotation_price}</div>
                  )}
                </div>
              </div>
              <div className="al-modal__foot" style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: "14px 20px", borderTop: "1px solid var(--color-border-soft)" }}>
                <button type="button" className="al-btn" onClick={() => setShowSupplierModal(false)}>Cancel</button>
                <button type="submit" className="al-btn al-btn--primary">Submit Quotation</button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default RevertBackShow;
